'use client';

import { GetTheatersByGeoRequest, GetTheatersByZipRequest, GetTheatersRequest } from '@/types/paged-requests/theater-paged-request';
import { commonFetch, CommonFetchResponse } from '@/utils/common-fetch';
import { buildUrlWithParams } from '@/utils/url-params';
import { PagedListOfTheater, Theater } from '@cinemark/content-api-client';
import { TheaterOperatingHours, TheaterSetting } from '@cinemark/theater-api-client';
import { format } from 'date-fns';

class TheatersApiService {
  private readonly theatersUrl = '/papi/theaters';
  private readonly errorMessage = 'Theater was not found';

  async getTheatersByZip(request: GetTheatersByZipRequest): Promise<CommonFetchResponse<PagedListOfTheater>> {
    const response = await commonFetch<PagedListOfTheater>(buildUrlWithParams(this.theatersUrl, request as unknown as Record<string, unknown>));
    return response;
  }

  async getTheatersByGeo(request: GetTheatersByGeoRequest): Promise<CommonFetchResponse<PagedListOfTheater>> {
    const response = await commonFetch<PagedListOfTheater>(buildUrlWithParams(this.theatersUrl, request as unknown as Record<string, unknown>));
    return response;
  }

  async getTheatersByTheaterIds(request: GetTheatersRequest): Promise<CommonFetchResponse<PagedListOfTheater>> {
    const response = await commonFetch<PagedListOfTheater>(buildUrlWithParams(this.theatersUrl, request as unknown as Record<string, unknown>));
    return response;
  }

  async getAllTheaters(): Promise<CommonFetchResponse<PagedListOfTheater>> {
    const response = await commonFetch<PagedListOfTheater>(buildUrlWithParams(this.theatersUrl, { pageSize: 1000 }));
    return response;
  }

  async getTheater(payloadId: string): Promise<Theater> {
    const response = await commonFetch<Theater>(`${this.theatersUrl}/${payloadId}`);
    return response?.data;
  }

  async getTheaterByTheaterId(theaterId: number): Promise<Theater> {
    const response = await commonFetch<PagedListOfTheater>(buildUrlWithParams(this.theatersUrl, { theaterIds: theaterId }));
    const theaters: PagedListOfTheater = response?.data;
    if (theaters?.data?.length) {
      return theaters.data[0];
    }

    throw new Error(this.errorMessage);
  }

  async getTheaterBySlug(slug: string): Promise<Theater> {
    const response = await commonFetch<PagedListOfTheater>(buildUrlWithParams(this.theatersUrl, { slug }));
    const theaters: PagedListOfTheater = response?.data;
    if (theaters?.data?.length) {
      return theaters.data[0];
    }

    throw new Error(this.errorMessage);
  }

  async getTheaterOperatingHours(theaterId: string, businessStartDate: Date, businessEndDate: Date, signal?: AbortSignal): Promise<TheaterOperatingHours[]> {
    const response = await commonFetch<TheaterOperatingHours[]>(
      buildUrlWithParams(`${this.theatersUrl}/${theaterId}/operating-hours`, {
        businessEndDate: format(businessEndDate, 'yyyy-MM-dd'),
        businessStartDate: format(businessStartDate, 'yyyy-MM-dd'),
      }), signal ? { signal } : undefined);

    return response?.data || [];
  }

  async getTheaterSettings(theaterId: string, signal?: AbortSignal): Promise<CommonFetchResponse<TheaterSetting[]>> {
    return await commonFetch<TheaterSetting[]>(`${this.theatersUrl}/${theaterId}/settings`, signal ? { signal } : undefined);
  }
}

export const theaterApiService = new TheatersApiService();
